import {
  GoAButton,
  GoAButtonGroup,
  GoACallout,
  GoAFormItem,
  GoAModal,
  GoARadioGroup,
  GoARadioItem,
  GoASpacer,
} from '@abgov/react-components';
import { BaseWizardProps } from '../wizard/wizard-step-base';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../state/store';
import {
  clearCollision,
  initialState,
  updateCollision,
} from '../../state/slices/collisionSlice';
import { Collision } from '../../models/collision';
import {
  pushNotification,
  removeAllValidationErrorNotifications,
} from '../../state/slices/notificationSlice';
import {
  defaultValidate,
  WizardButtonGroup,
} from '../wizard/wizard-button-group-component';
import { NotificationType } from '../notifications/notification-component';
import { IsInReviewEditMode } from '../wizard/wizard-component';
import {
  setCollisionSubTypeUpdated,
  setCollisionTypeUpdated,
} from '../../state/slices/configurationSlice';

export interface CollisionTypeComponentProps extends BaseWizardProps {}

const useAppDispatch = () => useDispatch<AppDispatch>();
const useAppSelector: <TSelected>(
  selector: (state: RootState) => TSelected,
) => TSelected = useSelector;

export const CollisionTypeComponent = ({
  navigationConfiguration,
}: CollisionTypeComponentProps) => {
  const dispatch = useAppDispatch();

  const defaultSelection = '0';
  const headerErrorText = 'Choose one collision type below';
  const errorText = 'Choose one collision type';
  const [formError, setFormError] = useState('');
  const [nextPage, setNextPage] = useState('');
  const stateConfiguration = useAppSelector(
    (state: RootState) => state.configuration,
  );
  const stateCollision = useAppSelector((state: RootState) => state.collision);

  //for tracking changes in collision type
  const [selection, setSelection] = useState(defaultSelection);
  const [hasShownModal, setHasShownModal] = useState(false);
  const [resetCollisionData, setResetCollisionData] = useState(false);
  const [
    hasShownConflictingEligibilityModal,
    setHasShownConflictingEligibilityModal,
  ] = useState(false);

  //for tracking displaying modal message when eligibility selection conflicts with collision type
  const [
    showModalConflictingEligibilitySelection,
    setShowModalConflictingEligibilitySelection,
  ] = useState(false);

  const [originalSelection, setOriginalSelection] = useState<
    string | undefined
  >(undefined);

  const [modalChangedText, setModalChangeText] = useState<string | undefined>(
    undefined,
  );

  useEffect(() => {
    if (navigationConfiguration.initializeState)
      navigationConfiguration.initializeState();

    if (IsInReviewEditMode()) {
      dispatch(
        pushNotification({
          body: "You can review or make changes to your inputs in this section. If you need to make changes, make sure to click 'Save and continue' to apply them.",
          notificationType: NotificationType.importantWarning,
        }),
      );

      dispatch(setCollisionTypeUpdated(false));
      dispatch(setCollisionSubTypeUpdated(false));
    }
  }, []);

  useEffect(() => {
    if (stateCollision.collision) {
      const collisionType = stateConfiguration.configuration.collisionType.find(
        (x) => x.id == stateCollision.collision.collisionType,
      );

      if (collisionType) {
        setSelection(collisionType.value ?? '0');
      } else {
        setSelection('0');
      }

      // Check if eligibility was hit-and-run but selected type is different
      if (
        !hasShownConflictingEligibilityModal &&
        selection !== '0' &&
        stateCollision.collision.wasHitAndRun &&
        selection !== 'hit-and-run'
      ) {
        setShowModalConflictingEligibilitySelection(true);
        setHasShownConflictingEligibilityModal(true); // Prevent repeated modal display
      }
    }
  }, [stateCollision]);

  useEffect(() => {
    setNextPage(`#${selection}`);
  }, [selection]);

  const save = () => {
    let updatedCollision: Collision = { ...stateCollision.collision };

    const collisionType = stateConfiguration.configuration.collisionType.find(
      (x) => x.value == selection,
    );

    updatedCollision.collisionType = collisionType?.id ?? 0;
    updatedCollision.collisionTypeFirstSaved = true;

    if (resetCollisionData) {
      //keep track of collision app start date
      const collsionTermsAndConditions =
        updatedCollision.agreementToTermsAndConditions;
      const collisionCreateDate = updatedCollision.createdAt;
      const collisionEligibility = updatedCollision.eligibility;
      const wasHitAndRun = updatedCollision.wasHitAndRun;
      const reportedDate = updatedCollision.reportedDate;
      dispatch(clearCollision());

      const metaDataMediaFormState = updatedCollision.metadata?.formState.find(
        (x) => x.name === 'Media Upload',
      );

      updatedCollision = {
        ...initialState.collision,
        id: updatedCollision.id,
        wasHitAndRun,
        agreementToTermsAndConditions: collsionTermsAndConditions,
        collisionType: updatedCollision.collisionType,
        createdAt: collisionCreateDate,
        eligibility: collisionEligibility,
        reportedDate: reportedDate,
      };

      const finalFormState = updatedCollision.metadata!.formState.map(
        (item) => {
          if (item.name === 'Media Upload') {
            return metaDataMediaFormState ?? item;
          } else {
            return item;
          }
        },
      );

      updatedCollision.metadata = {
        ...updatedCollision.metadata,
        formState: finalFormState,
      };
    }

    if (
      navigationConfiguration.updateMetadata &&
      navigationConfiguration.stepId
    ) {
      const stepUpdated = navigationConfiguration.updateMetadata(
        updatedCollision,
        navigationConfiguration.stepId!,
        navigationConfiguration.stageCompleted
          ? navigationConfiguration.stageCompleted
          : false,
        !navigationConfiguration.stageCompleted,
      );

      if (stepUpdated && updatedCollision.metadata) {
        updatedCollision.metadata = {
          ...updatedCollision.metadata,
          formState: stepUpdated,
        };
      }
    }

    dispatch(updateCollision(updatedCollision));

    if (navigationConfiguration.save) {
      navigationConfiguration.save();
    }
  };

  useEffect(() => {
    const handleBeforeUnload = () => {
      setHasShownModal(false);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const handleCollisionTypeChange = (
    item: string,
    selectedCollisionType: string,
  ) => {
    const existingCollisionTypeId = stateCollision.collision.collisionType;

    const existingCollisionType =
      stateConfiguration.configuration.collisionType.find(
        (x) => x.id === existingCollisionTypeId,
      )?.value;

    setSelection(selectedCollisionType);
    setOriginalSelection(existingCollisionType);

    const isSwitchingFromHitAndRun =
      stateCollision.collision.wasHitAndRun &&
      selectedCollisionType !== 'hit-and-run';

    const isSwitchingToHitAndRun =
      !stateCollision.collision.wasHitAndRun &&
      selectedCollisionType === 'hit-and-run';

    const isExistingTypeConflicting =
      existingCollisionType !== undefined &&
      ((selectedCollisionType === 'hit-and-run' &&
        existingCollisionType !== 'hit-and-run' &&
        !stateCollision.collision.wasHitAndRun) ||
        (selectedCollisionType !== 'hit-and-run' &&
          existingCollisionType === 'hit-and-run' &&
          stateCollision.collision.wasHitAndRun));

    const shouldShowModal =
      !hasShownConflictingEligibilityModal &&
      (isSwitchingFromHitAndRun ||
        isSwitchingToHitAndRun ||
        isExistingTypeConflicting);

    if (shouldShowModal) {
      setShowModalConflictingEligibilitySelection(true);
      setHasShownConflictingEligibilityModal(true);
      applyCollisionTypeChange(selectedCollisionType);
      setResetCollisionData(true);
    } else {
      applyCollisionTypeChange(selectedCollisionType);
      setResetCollisionData(false);
    }

    if (
      existingCollisionType &&
      existingCollisionType !== selectedCollisionType
    ) {
      dispatch(setCollisionTypeUpdated(true));
      dispatch(setCollisionSubTypeUpdated(false));
    }
  };

  const applyCollisionTypeChange = (id: string) => {
    let updatedCollision: Collision = { ...stateCollision.collision };

    const collisionType = stateConfiguration.configuration.collisionType.find(
      (x) => x.value === id,
    );
    updatedCollision.collisionType = collisionType?.id ?? 0;

    setSelection(id);
    setNextPage(`#${id}`);
    setFormError('');

    dispatch(removeAllValidationErrorNotifications());
  };

  return (
    <div>
      <GoAModal
        calloutVariant="important"
        heading={
          stateCollision.collision.wasHitAndRun
            ? 'You indicated a parked vehicle hit and run collision in the eligibility questions'
            : "You didn't indicate a parked vehicle hit and run collision in the eligibility questions"
        }
        open={showModalConflictingEligibilitySelection}
        actions={
          <div style={{ textAlign: 'left' }}>
            <ul style={{ marginTop: '-15px' }}>
              <li>
                If you are just starting a new report now is the time to
                re-confirm your collision type.
              </li>
              <li>
                If you have completed previous reporting steps, changing your
                collision type will require you to restart your collision report
                from this page.{' '}
              </li>
            </ul>

            <div>
              <GoAButtonGroup alignment="end" mt="xl">
                <GoAButton
                  type="primary"
                  onClick={() => {
                    //hide the modal and let the user continue
                    setShowModalConflictingEligibilitySelection(false);
                  }}
                >
                  Close
                </GoAButton>
              </GoAButtonGroup>
            </div>
          </div>
        }
      ></GoAModal>
      <h1>Collision type</h1>
      <p>
        The collision details section is a starting point for drivers to
        identify the type of collision they were involved in. In some cases
        you'll be prompted to add some additional details depending on your
        collision type.
      </p>
      <GoASpacer vSpacing="xl" />
      <GoACallout heading="Important" type="important">
        Report My Collision currently only supports limited collision types
        listed below. Consult the eligibility questions for more information.
      </GoACallout>
      <a href="/collision">Take me to the eligibility questions</a>
      <h2>What type of collision were you in?</h2>
      <GoASpacer vSpacing="2xs" />
      Select a collision type below that best fits what occured
      <GoASpacer vSpacing="xl" />
      <GoAFormItem error={formError}>
        <GoARadioGroup
          name="collisionTypeGroup"
          error={(formError || '').length > 0}
          value={selection}
          onChange={(item, id) => {
            handleCollisionTypeChange(item, id);
          }}
        >
          <GoARadioItem
            value="animal"
            label="My vehicle hit an animal"
          ></GoARadioItem>
          <GoARadioItem
            value="hit-and-run"
            label="My parked vehicle was involved in a hit and run"
            description={
              <span style={{ font: 'var(--goa-typography-body-xs)' }}>
                Note: When no supporting information is available
              </span>
            }
          ></GoARadioItem>
          <GoARadioItem
            value="object-on-roadway"
            label="My vehicle hit an object on roadway"
          ></GoARadioItem>
          <GoARadioItem
            value="left-side-road"
            label="My vehicle ran off the left side of the road"
          ></GoARadioItem>
          <GoARadioItem
            value="right-side-road"
            label="My vehicle ran off the right side of the road"
          ></GoARadioItem>
        </GoARadioGroup>
      </GoAFormItem>
      <WizardButtonGroup
        nextPage={nextPage}
        validate={(): boolean => {
          return defaultValidate(
            selection,
            defaultSelection,
            errorText,
            setFormError,
            () => {
              dispatch(removeAllValidationErrorNotifications());
              dispatch(
                pushNotification({
                  body: headerErrorText,
                  notificationType: NotificationType.importantValidationError,
                }),
              );
            },
          );
        }}
        showViewSummaryButton={!IsInReviewEditMode()}
        goToReview={navigationConfiguration.goToReview}
        save={save}
        navigate={navigationConfiguration.navigate}
      />
    </div>
  );
};
